import { useEffect, useState } from 'react'
import type { ScheduledJobView, ScheduledRun } from '@numeo/types'
import { Spinner } from '../_ds/Primitives'
import { schedulersApi } from './api'
import { formatDuration, formatTime, OUTCOME_CLASS, OUTCOME_LABEL } from './format'

interface JobRunsProps {
    job: ScheduledJobView
    historyHours: number
}

/** The expanded part of a job row: what it calls, what starts it, and its runs over the history window. */
export function JobRuns({ job, historyHours }: JobRunsProps) {
    const [runs, setRuns] = useState<ScheduledRun[] | null>(null)
    const [error, setError] = useState('')

    useEffect(() => {
        let cancelled = false
        schedulersApi
            .runs(job.name)
            .then((data) => !cancelled && setRuns(data.runs))
            .catch(() => !cancelled && setError('Could not load the run history'))
        return () => {
            cancelled = true
        }
    }, [job.name])

    return (
        <div className="space-y-3 bg-slate-50 px-4 py-3 text-xs dark:bg-slate-900/40">
            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                <dt className="text-slate-500">Calls</dt>
                <dd className="font-mono text-slate-700 dark:text-slate-300">{job.calls}</dd>
                <dt className="text-slate-500">Started when</dt>
                <dd className="font-mono text-slate-700 dark:text-slate-300">{job.gate}</dd>
                {job.timeoutMs ? (
                    <>
                        <dt className="text-slate-500">Timeout</dt>
                        <dd className="text-slate-700 dark:text-slate-300">{formatDuration(job.timeoutMs)}</dd>
                    </>
                ) : null}
            </dl>

            <div>
                <p className="mb-1.5 font-semibold text-slate-600 dark:text-slate-300">Runs in the last {historyHours} hours</p>
                {error && <p className="text-rose-600">{error}</p>}
                {!error && runs === null && <Spinner />}
                {runs?.length === 0 && <p className="text-slate-500">No runs recorded.</p>}
                {runs && runs.length > 0 && (
                    <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                        <table className="w-full">
                            <thead className="sticky top-0 bg-white text-left text-[11px] uppercase tracking-wider text-slate-400 dark:bg-slate-900">
                                <tr>
                                    <th className="px-3 py-1.5 font-semibold">Started</th>
                                    <th className="px-3 py-1.5 font-semibold">Trigger</th>
                                    <th className="px-3 py-1.5 font-semibold">Outcome</th>
                                    <th className="px-3 py-1.5 font-semibold">Duration</th>
                                    <th className="px-3 py-1.5 font-semibold">Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {/* Two API records can share a millisecond, and the list is replaced whole on each load, so the index makes the key unique. */}
                                {runs.map((run, index) => (
                                    <tr key={`${index}-${run.startedAt}-${run.trigger}`}>
                                        <td className="whitespace-nowrap px-3 py-1.5 text-slate-600 dark:text-slate-300">{formatTime(run.startedAt)}</td>
                                        <td className="px-3 py-1.5 text-slate-500">{run.trigger}</td>
                                        <td className={`px-3 py-1.5 font-medium ${OUTCOME_CLASS[run.outcome]}`}>{OUTCOME_LABEL[run.outcome]}</td>
                                        <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{formatDuration(run.durationMs)}</td>
                                        <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300">
                                            {run.error ? <span className="text-rose-600 dark:text-rose-400">{run.error}</span> : run.summary}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
